import { Picking } from "api/wms/models";
import { Tag } from "components/miloDesignSystem/atoms/tag";
import { Typography } from "components/miloDesignSystem/atoms/typography";
import { useCreateTableColumns } from "utilities/tableColumnsUtilities/createTableColumns/createTableColumns";
import { PickingFinishDate } from "../components/PickingFinishDate";
import { dateFns, pluralize } from "utilities";
import { PickingStartDate } from "../components/PickingStartDate";
import { parse } from "date-fns";
import { formatHours } from "pages/wms/shared/utils/formatHours";
import { formatMinutes } from "pages/wms/shared/utils/formatMinutes";
import { useDownloadPickingPdf } from "./useDownloadPickingPdf";
import { IconButton } from "components/miloDesignSystem/atoms/iconButton";
import { MdiQrCode } from "components/miloDesignSystem/atoms/icons/MdiQrCode";

export const usePickingsColumns = () => {
  const handleDownloadPickingPdf = useDownloadPickingPdf();

  return useCreateTableColumns<Picking>(({ columnHelper }) => {
    return [
      columnHelper.accessor(row => row, {
        header: "sygnatura",
        size: 200,
        cell: info => {
          const picking = info.getValue();
          return (
            <div className="d-flex align-items-center gap-2">
              <Typography fontSize="14" fontWeight="700">
                {picking.signature}
              </Typography>
              {picking.pauseStartedAt && !picking.pauseEndedAt && (
                <Tag label="Trwa pauza" variant="warning" />
              )}
            </div>
          );
        },
      }),
      columnHelper.text(row => row.suppliers.join(", "), {
        header: "producenci",
        size: 250,
      }),
      columnHelper.accessor(row => row.ramp, {
        header: "rampa",
        size: 40,
        cell: info => {
          if (!info.getValue())
            return (
              <Typography
                className="d-flex justify-content-center w-100"
                fontSize="12"
                fontWeight="700"
              >
                ---
              </Typography>
            );
          return (
            <div className="d-flex justify-content-center w-100">
              <Tag label={info.getValue().name} variant="warning" />
            </div>
          );
        },
      }),
      columnHelper.accessor(row => row.estimatedTimeOfLoading, {
        id: "loading",
        header: () => (
          <Typography
            className="text-right w-100"
            color="neutralBlack48"
            fontSize="12"
            fontWeight="400"
          >
            załadunek
          </Typography>
        ),
        size: 90,
        cell: info => {
          return (
            <Typography className="text-right w-100" fontSize="12" fontWeight="700">
              {!info.getValue() ? "---" : `${dateFns.format(new Date(info.getValue()), "H:mm")}`}
            </Typography>
          );
        },
      }),
      columnHelper.accessor(row => row, {
        header: "start",
        size: 120,
        cell: info => {
          return <PickingStartDate fontSize="12" fontWeight="700" picking={info.getValue()} />;
        },
      }),
      columnHelper.accessor(row => row, {
        header: "zakończenie",
        size: 90,
        cell: info => {
          return <PickingFinishDate fontSize="12" fontWeight="700" picking={info.getValue()} />;
        },
      }),
      columnHelper.text(
        row => {
          if (!row.duration || !Boolean(row.duration.length)) return null;

          const matchDays = row.duration.match(/^\d+\s/);

          const parsedTime = parse(
            matchDays ? row.duration.slice(matchDays[0].length) : row.duration,
            "HH:mm:ss.SSSSSS",
            new Date(),
          );

          if (matchDays) {
            const days = parseInt(matchDays[0], 10);
            return `${days} ${pluralize.pl(days, {
              singular: "dzień",
              plural: "dni",
              other: "dni",
            })}, ${formatHours(parsedTime)} ${formatMinutes(parsedTime)}`;
          }

          return `${formatHours(parsedTime)} ${formatMinutes(parsedTime)}`;
        },
        {
          header: "czas trwania",
          size: 90,
        },
      ),
      columnHelper.accessor(row => row.numberOfPickedUp, {
        header: "skompletowane zam.",
        size: 120,
        cell: info => {
          const numberOfPickedUp = info.getValue();
          return (
            <div className="d-flex align-items-center">
              <Typography color="success500" fontSize="14" fontWeight="700">
                {Number(numberOfPickedUp.onRamp)}
              </Typography>
              <Typography fontSize="14" fontWeight="700">
                /{Number(numberOfPickedUp.total)}
              </Typography>
            </div>
          );
        },
      }),
      columnHelper.accessor(row => row, {
        header: "etykiety",
        size: 43,
        cell: info => {
          const picking = info.getValue();
          return (
            <div className="d-flex justify-content-center w-100">
              <IconButton
                icon={<MdiQrCode size="16" />}
                onClick={event => {
                  event.stopPropagation();
                  handleDownloadPickingPdf(picking);
                }}
                variant="blackST"
              />
            </div>
          );
        },
      }),
    ];
  });
};
